import { AppButton } from "../components/ui/AppButton.js";
import {
  SHELL_FAILURE_COPY,
  SHELL_MOCK_CART,
  SHELL_MOCK_CONTACT,
  SHELL_MOCK_DELIVERY,
  SHELL_MOCK_DRAFT,
  SHELL_MOCK_PAYMENT,
  SHELL_MOCK_TG50,
  SHELL_MOCK_TIMELINE,
} from "./mock-data.js";
import { BlockedChip, FailureCard, ShellPanel } from "./ShellChrome.js";
import type { ShellFailureKind, ShellStepId } from "./types.js";

export function CartReviewScreen() {
  return (
    <ShellPanel title="Cart review">
      <ul className="flex flex-col gap-3" role="list">
        {SHELL_MOCK_CART.map((line) => (
          <li
            key={line.id}
            className="flex items-start justify-between gap-3 rounded-[14px] border border-black/5 p-3"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium">{line.title}</p>
              <p className="text-xs opacity-60">{line.skuLabel}</p>
              <p className="mt-1 text-xs">Qty: {line.qty}</p>
            </div>
            <p className="shrink-0 text-xs font-medium opacity-70">{line.amountLabel}</p>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-sm font-medium">Total: — (test order, not a sale)</p>
    </ShellPanel>
  );
}

export function ContactFormScreen() {
  return (
    <ShellPanel title="Customer contact">
      <label className="mb-3 flex flex-col gap-1 text-sm">
        <span>Name</span>
        <input
          className="min-h-11 rounded-[14px] border border-black/10 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] px-3"
          value={SHELL_MOCK_CONTACT.fullName}
          readOnly
        />
      </label>
      <label className="mb-3 flex flex-col gap-1 text-sm">
        <span>Phone</span>
        <input
          className="min-h-11 rounded-[14px] border border-black/10 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] px-3"
          value={SHELL_MOCK_CONTACT.phone}
          readOnly
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span>Note</span>
        <textarea
          className="rounded-[14px] border border-black/10 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] px-3 py-2"
          rows={2}
          value={SHELL_MOCK_CONTACT.note}
          readOnly
        />
      </label>
      <p className="mt-2 text-xs opacity-60">Mock fields only — no real customer data.</p>
    </ShellPanel>
  );
}

export function DeliveryMethodScreen() {
  return (
    <ShellPanel title="Delivery method">
      <select
        className="min-h-11 w-full rounded-[14px] border border-black/10 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] px-3"
        disabled
        defaultValue="np"
      >
        <option value="np">{SHELL_MOCK_DELIVERY.methodLabel}</option>
        <option value="pickup">Pickup (placeholder)</option>
      </select>
      <p className="mt-2 text-xs opacity-60">Selection disabled — wire after M60 + live delivery APIs.</p>
    </ShellPanel>
  );
}

export function NovaPoshtaPlaceholderScreen() {
  return (
    <div className="flex flex-col gap-3">
      <ShellPanel title="Nova Poshta city / warehouse">
        <label className="mb-3 flex flex-col gap-1 text-sm">
          <span>City</span>
          <input
            className="min-h-11 rounded-[14px] border border-black/10 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] px-3"
            value={SHELL_MOCK_DELIVERY.cityLabel}
            readOnly
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span>Warehouse</span>
          <input
            className="min-h-11 rounded-[14px] border border-black/10 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] px-3"
            value={SHELL_MOCK_DELIVERY.warehouseLabel}
            readOnly
          />
        </label>
      </ShellPanel>
      <FailureCard {...SHELL_FAILURE_COPY.nova_poshta_unavailable} />
    </div>
  );
}

export function PaymentOptionScreen() {
  return (
    <div className="flex flex-col gap-3">
      <ShellPanel title="Payment option">
        <select
          className="min-h-11 w-full rounded-[14px] border border-black/10 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] px-3"
          disabled
          defaultValue="placeholder"
        >
          <option value="placeholder">{SHELL_MOCK_PAYMENT.optionLabel}</option>
        </select>
        <p className="mt-2 text-sm opacity-70">{SHELL_MOCK_PAYMENT.statusLabel}</p>
      </ShellPanel>
      <FailureCard {...SHELL_FAILURE_COPY.payment_unavailable} />
    </div>
  );
}

export function Tg50CouponScreen() {
  return (
    <ShellPanel title="TG50 coupon">
      <label className="mb-3 flex flex-col gap-1 text-sm">
        <span>Coupon code</span>
        <input
          className="min-h-11 rounded-[14px] border border-black/10 px-3"
          placeholder="Enter code (UI only)"
          defaultValue={SHELL_MOCK_TG50.codeInput}
          disabled
        />
      </label>
      <div className="flex items-center gap-2 text-sm">
        <BlockedChip label="Unproven" />
        <span className="opacity-70">{SHELL_MOCK_TG50.statusLabel}</span>
      </div>
      <AppButton className="mt-3 w-full" disabled>
        Apply promo
      </AppButton>
    </ShellPanel>
  );
}

export function OrderReviewScreen() {
  return (
    <ShellPanel title="Order review">
      <dl className="space-y-2 text-sm">
        <div className="flex justify-between gap-3">
          <dt className="opacity-60">Items</dt>
          <dd>{SHELL_MOCK_CART.length} demo lines</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="opacity-60">Contact</dt>
          <dd>{SHELL_MOCK_CONTACT.fullName}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="opacity-60">Delivery</dt>
          <dd className="text-right">{SHELL_MOCK_DELIVERY.methodLabel}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="opacity-60">Payment</dt>
          <dd className="text-right">{SHELL_MOCK_PAYMENT.optionLabel}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="opacity-60">Payable</dt>
          <dd>— (not priced)</dd>
        </div>
      </dl>
      <AppButton className="mt-4 w-full" disabled>
        Place order
      </AppButton>
    </ShellPanel>
  );
}

export function OrderDraftCreatedScreen() {
  return (
    <ShellPanel title="Order draft created (placeholder)">
      <p className="text-sm font-medium">{SHELL_MOCK_DRAFT.draftLabel}</p>
      <p className="mt-2 text-sm opacity-70">{SHELL_MOCK_DRAFT.message}</p>
      <FailureCard
        title={SHELL_FAILURE_COPY.checkout_unavailable.title}
        body={SHELL_FAILURE_COPY.checkout_unavailable.body}
      />
    </ShellPanel>
  );
}

export function PaymentPendingScreen() {
  return (
    <div className="flex flex-col gap-3">
      <ShellPanel title="Payment pending">
        <p className="text-sm">Status: pending (UI placeholder)</p>
        <p className="mt-2 text-xs opacity-60">No payment provider calls. No paid claim.</p>
        <AppButton className="mt-3 w-full" disabled>
          Open payment link (unavailable)
        </AppButton>
      </ShellPanel>
      <FailureCard {...SHELL_FAILURE_COPY.payment_unavailable} />
    </div>
  );
}

export function OrderStatusTimelineScreen() {
  return (
    <ShellPanel title="Order status timeline">
      <ol className="flex flex-col gap-3">
        {SHELL_MOCK_TIMELINE.map((step) => (
          <li
            key={step.id}
            className="flex items-start gap-3 rounded-[14px] border border-black/5 p-3"
          >
            <span
              className={`mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full ${
                step.state === "done"
                  ? "bg-emerald-500"
                  : step.state === "current"
                    ? "bg-sky-500"
                    : step.state === "blocked"
                      ? "bg-amber-500"
                      : "bg-black/20"
              }`}
            />
            <div className="min-w-0">
              <p className="text-sm font-medium">{step.label}</p>
              <p className="text-xs capitalize opacity-60">{step.state}</p>
              {step.note ? <p className="mt-1 text-xs opacity-70">{step.note}</p> : null}
            </div>
          </li>
        ))}
      </ol>
    </ShellPanel>
  );
}

const FAILURE_ORDER: ShellFailureKind[] = [
  "supabase_unavailable",
  "checkout_unavailable",
  "payment_unavailable",
  "nova_poshta_unavailable",
  "stock_verification_required",
  "manager_confirmation_required",
];

export function FailureGalleryScreen() {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm opacity-70">Preview of blocked / unproven customer states (UI only).</p>
      {FAILURE_ORDER.map((kind) => (
        <FailureCard key={kind} {...SHELL_FAILURE_COPY[kind]} />
      ))}
    </div>
  );
}

export function renderShellStep(step: ShellStepId) {
  switch (step) {
    case "cart_review":
      return <CartReviewScreen />;
    case "contact":
      return <ContactFormScreen />;
    case "delivery_method":
      return <DeliveryMethodScreen />;
    case "nova_poshta":
      return <NovaPoshtaPlaceholderScreen />;
    case "payment_option":
      return <PaymentOptionScreen />;
    case "tg50":
      return <Tg50CouponScreen />;
    case "order_review":
      return <OrderReviewScreen />;
    case "order_draft":
      return <OrderDraftCreatedScreen />;
    case "payment_pending":
      return <PaymentPendingScreen />;
    case "order_status":
      return <OrderStatusTimelineScreen />;
    case "failure_gallery":
      return <FailureGalleryScreen />;
    default:
      return null;
  }
}
